import { useEffect, useRef, useCallback } from 'react'

/**
 * Song form state for localStorage persistence
 */
export interface SongFormState {
  prompt: string
  lyrics: string
  voiceId: string
  title: string
  tags: string
  makeInstrumental: boolean
}

const STORAGE_KEY = 'jinglearn_song_form_state'
const AUTO_SAVE_INTERVAL = 30000 // 30 seconds

/**
 * Custom hook for managing song form state in localStorage
 *
 * Features:
 * - Auto-saves on blur events (when focus leaves input)
 * - Auto-saves every 30 seconds if changes detected
 * - Only saves if value has changed, length > 0, or is a boolean
 * - Loads saved state on mount
 * - Provides clearAll function to reset form and localStorage
 *
 * @param userId - Current user ID (used as part of storage key)
 * @returns Object with save, load, and clearAll functions
 */
export function useSongFormStorage(userId: string) {
  const storageKey = `${STORAGE_KEY}_${userId}`
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedStateRef = useRef<SongFormState | null>(null)

  /**
   * Load saved form state from localStorage
   */
  const loadFormState = useCallback((): SongFormState | null => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = JSON.parse(saved) as SongFormState
        lastSavedStateRef.current = parsed
        return parsed
      }
    } catch (error) {
      console.error('Failed to load song form state from localStorage:', error)
    }
    return null
  }, [storageKey])

  /**
   * Save form state to localStorage
   * Only saves if values have changed from last saved state
   */
  const saveFormState = useCallback((state: SongFormState) => {
    try {
      // Check if state has actually changed
      const lastSaved = lastSavedStateRef.current
      if (lastSaved && JSON.stringify(lastSaved) === JSON.stringify(state)) {
        return // No changes, skip save
      }

      // Filter out empty strings but keep booleans
      const stateToSave: Partial<SongFormState> = {}

      if (state.prompt && state.prompt.length > 0) {
        stateToSave.prompt = state.prompt
      }
      if (state.lyrics && state.lyrics.length > 0) {
        stateToSave.lyrics = state.lyrics
      }
      if (state.voiceId && state.voiceId.length > 0) {
        stateToSave.voiceId = state.voiceId
      }
      if (state.title && state.title.length > 0) {
        stateToSave.title = state.title
      }
      if (state.tags && state.tags.length > 0) {
        stateToSave.tags = state.tags
      }
      // Always save boolean value
      stateToSave.makeInstrumental = state.makeInstrumental

      localStorage.setItem(storageKey, JSON.stringify(stateToSave))
      lastSavedStateRef.current = state
    } catch (error) {
      console.error('Failed to save song form state to localStorage:', error)
    }
  }, [storageKey])

  /**
   * Clear all form state from localStorage
   */
  const clearFormState = useCallback(() => {
    try {
      localStorage.removeItem(storageKey)
      lastSavedStateRef.current = null
    } catch (error) {
      console.error('Failed to clear song form state from localStorage:', error)
    }
  }, [storageKey])

  /**
   * Setup auto-save interval (every 30 seconds)
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current)
      }
    }
  }, [])

  /**
   * Create auto-save timer function
   */
  const startAutoSave = useCallback((getState: () => SongFormState) => {
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current)
    }

    // Setup new timer
    autoSaveTimerRef.current = setInterval(() => {
      const currentState = getState()
      saveFormState(currentState)
    }, AUTO_SAVE_INTERVAL)
  }, [saveFormState])

  return {
    loadFormState,
    saveFormState,
    clearFormState,
    startAutoSave,
  }
}
